/**
 * The screens, rendered from a `GameModel` snapshot. Twin of
 * `app/lib/screens.dart`.
 *
 * There is no framework. The whole navigation of the app is one field —
 * `phase` — and this file re-renders the root element on every change.
 * At the sizes involved (a handful of divs, one canvas, twenty leaderboard
 * rows) it is cheaper than any diffing layer would be.
 */

import { GameModel, type Phase } from './game.js';
import { BoardRenderer } from './board.js';
import { bindKeyboard, bindSwipe, EAST, NORTH, SOUTH, WEST } from './input.js';
import type { LeaderboardEntry } from '../../shared/protocol.js';

interface RaceView {
  canvas: HTMLCanvasElement;
  renderer: BoardRenderer;
  unbindKeys: () => void;
  unbindSwipe: () => void;
  hud: {
    you: HTMLElement;
    opponent: HTMLElement;
  };
  verdict: HTMLDivElement;
  container: HTMLElement;
}

/**
 * Mount the whole UI onto a root element and start rendering from a model.
 *
 * Returns a `dispose` that pulls listeners and detaches DOM, so tests can
 * mount and unmount without leaking timers.
 */
export function mount(
  root: HTMLElement,
  model: GameModel,
): { dispose: () => void } {
  let race: RaceView | null = null;
  let boardResizeObserver: ResizeObserver | null = null;

  const render = (): void => {
    // Playing is the one phase where the DOM is expensive to rebuild — the
    // <canvas> and its ResizeObserver — so the switch has two branches: the
    // race gets patched in place, everything else is a fresh replacement.
    if (model.phase === 'playing' || model.phase === 'finished') {
      if (race === null) race = mountRace(root, model);
      updateHud(race, model);
      race.renderer.render();
      updateVerdict(race, model);
    } else {
      if (race !== null) {
        race.unbindKeys();
        race.unbindSwipe();
        boardResizeObserver?.disconnect();
        boardResizeObserver = null;
        race = null;
      }
      root.replaceChildren(renderNonRace(model));
    }
  };

  const unsubscribe = model.onChange(render);
  render();

  function mountRace(container: HTMLElement, model: GameModel): RaceView {
    container.replaceChildren();
    const wrap = document.createElement('div');
    wrap.className = 'race';

    const notice = renderNotice(model);
    if (notice !== null) wrap.appendChild(notice);

    const boardHolder = document.createElement('div');
    boardHolder.className = 'board-holder';
    const canvas = document.createElement('canvas');
    canvas.className = 'board';
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label', 'maze');
    boardHolder.appendChild(canvas);

    const verdict = document.createElement('div');
    verdict.className = 'verdict';
    verdict.style.display = 'none';
    boardHolder.appendChild(verdict);

    const hudEl = document.createElement('div');
    hudEl.className = 'hud';
    const youEl = document.createElement('span');
    youEl.className = 'you';
    const oppEl = document.createElement('span');
    oppEl.className = 'opponent';
    hudEl.appendChild(youEl);
    hudEl.appendChild(oppEl);

    // The race layout: pad beside the board on wide landscape, underneath
    // otherwise. Same decision the Flutter app makes.
    const layout = document.createElement('div');
    layout.className = 'race-layout';
    layout.appendChild(boardHolder);
    layout.appendChild(renderPad(model));

    wrap.appendChild(hudEl);
    wrap.appendChild(layout);
    wrap.appendChild(renderRaceControls(model));

    container.appendChild(wrap);

    const renderer = new BoardRenderer(canvas, model);
    // The canvas is sized by CSS. `ResizeObserver` sets the backing store to
    // the pixel size the layout gave us so the drawing matches — otherwise a
    // stretched canvas produces blurred walls and pawns.
    const observer = new ResizeObserver(() => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const w = Math.max(1, Math.floor(rect.width * dpr));
      const h = Math.max(1, Math.floor(rect.height * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        renderer.invalidate();
        renderer.render();
      }
    });
    observer.observe(canvas);
    boardResizeObserver = observer;

    const unbindKeys = bindKeyboard(model);
    const unbindSwipe = bindSwipe(canvas, model);

    return {
      canvas,
      renderer,
      unbindKeys,
      unbindSwipe,
      hud: { you: youEl, opponent: oppEl },
      verdict,
      container,
    };
  }

  return {
    dispose: () => {
      unsubscribe();
      race?.unbindKeys();
      race?.unbindSwipe();
      boardResizeObserver?.disconnect();
      root.replaceChildren();
    },
  };
}

function renderNonRace(model: GameModel): HTMLElement {
  switch (model.phase) {
    case 'idle':
    case 'lost':
      return renderMenu(model);
    case 'connecting':
    case 'queued':
      return renderLobby(model);
    case 'leaderboardLoading':
    case 'leaderboard':
      return renderLeaderboard(model);
    default:
      return renderMenu(model);
  }
}

function renderMenu(model: GameModel): HTMLElement {
  const screen = document.createElement('div');
  screen.className = 'screen';

  const title = document.createElement('h1');
  title.className = 'title';
  title.textContent = 'Labrace';
  screen.appendChild(title);

  const subtitle = document.createElement('div');
  subtitle.className = 'subtitle';
  subtitle.textContent = 'Race to the centre. The maze is fair.';
  screen.appendChild(subtitle);

  const notice = renderNotice(model);
  if (notice !== null) screen.appendChild(notice);

  const nameLabel = document.createElement('label');
  nameLabel.setAttribute('for', 'labrace-name');
  nameLabel.textContent = 'Your name';
  screen.appendChild(nameLabel);
  const name = document.createElement('input');
  name.type = 'text';
  name.id = 'labrace-name';
  name.autocomplete = 'given-name';
  name.spellcheck = false;
  name.maxLength = 16;
  name.placeholder = 'Player';
  name.value = model.playerName;
  name.addEventListener('input', () => {
    model.playerName = name.value;
  });
  screen.appendChild(name);

  const playRandom = document.createElement('button');
  playRandom.textContent = 'Play random';
  playRandom.addEventListener('click', () => {
    void model.play(name.value);
  });
  screen.appendChild(playRandom);

  const friendWrap = document.createElement('div');
  friendWrap.style.display = 'flex';
  friendWrap.style.flexDirection = 'column';
  friendWrap.style.width = '100%';
  friendWrap.style.gap = '8px';

  const friendLabel = document.createElement('label');
  friendLabel.setAttribute('for', 'labrace-friend');
  friendLabel.textContent = 'Or play with a friend by name';
  friendWrap.appendChild(friendLabel);

  const friend = document.createElement('input');
  friend.type = 'text';
  friend.id = 'labrace-friend';
  friend.autocomplete = 'off';
  friend.spellcheck = false;
  friend.maxLength = 16;
  friend.placeholder = 'Their name';
  friend.value = model.friendName;
  friend.addEventListener('input', () => {
    model.friendName = friend.value;
  });
  friendWrap.appendChild(friend);

  const playFriend = document.createElement('button');
  playFriend.className = 'secondary';
  playFriend.textContent = 'Play with a friend';
  playFriend.addEventListener('click', () => {
    if (friend.value.trim() === '') return;
    void model.play(name.value, friend.value);
  });
  friendWrap.appendChild(playFriend);
  screen.appendChild(friendWrap);

  const leaderboard = document.createElement('button');
  leaderboard.className = 'ghost';
  leaderboard.textContent = 'Leaderboard';
  leaderboard.addEventListener('click', () => {
    void model.openLeaderboard();
  });
  screen.appendChild(leaderboard);

  // Support and privacy sit at the bottom of the menu, outside the flow of
  // play. The policy link opens a new tab so the menu (and any pending game)
  // is not navigated away from; `policy.html` ships in `web/public/` and Vite
  // copies it to the site root next to `index.html`.
  const footer = document.createElement('div');
  footer.className = 'menu-footer';

  const contact = document.createElement('a');
  contact.href = 'mailto:emre@sener.net.tr';
  contact.textContent = 'Contact support';
  footer.appendChild(contact);

  const privacy = document.createElement('a');
  privacy.href = '/policy.html';
  privacy.target = '_blank';
  privacy.rel = 'noopener noreferrer';
  privacy.textContent = 'Privacy policy';
  footer.appendChild(privacy);

  screen.appendChild(footer);

  return screen;
}

function renderLobby(model: GameModel): HTMLElement {
  const screen = document.createElement('div');
  screen.className = 'screen';

  const title = document.createElement('h1');
  title.className = 'title';
  title.style.fontSize = '28px';
  title.textContent =
    model.waitingFor !== null
      ? `Waiting for ${model.waitingFor}`
      : 'Finding an opponent';
  screen.appendChild(title);

  const spinner = document.createElement('div');
  spinner.className = 'spinner';
  screen.appendChild(spinner);

  const notice = renderNotice(model);
  if (notice !== null) screen.appendChild(notice);

  const cancel = document.createElement('button');
  cancel.className = 'secondary';
  cancel.textContent = 'Cancel';
  cancel.addEventListener('click', () => {
    // Same as the app: `resign` is what leaves a queue too.
    model.resign();
    model.backToMenu();
  });
  screen.appendChild(cancel);

  return screen;
}

function renderLeaderboard(model: GameModel): HTMLElement {
  const screen = document.createElement('div');
  screen.className = 'screen';

  const top = document.createElement('div');
  top.className = 'top-bar';
  const back = document.createElement('button');
  back.className = 'ghost';
  back.textContent = '←';
  back.style.width = 'auto';
  back.setAttribute('aria-label', 'Back');
  back.addEventListener('click', () => model.closeLeaderboard());
  top.appendChild(back);
  const title = document.createElement('h2');
  title.textContent = 'Leaderboard';
  top.appendChild(title);
  screen.appendChild(top);

  const notice = renderNotice(model);
  if (notice !== null) screen.appendChild(notice);

  if (model.phase === 'leaderboardLoading') {
    const spinner = document.createElement('div');
    spinner.className = 'spinner';
    screen.appendChild(spinner);
    return screen;
  }

  if (model.leaderboard.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'subtitle';
    empty.textContent = 'No completed games yet.';
    screen.appendChild(empty);
    return screen;
  }

  const list = document.createElement('div');
  list.className = 'leaderboard';
  model.leaderboard.forEach((row, i) => {
    list.appendChild(renderLeaderRow(i + 1, row));
  });
  screen.appendChild(list);
  return screen;
}

function renderLeaderRow(rank: number, entry: LeaderboardEntry): HTMLElement {
  const row = document.createElement('div');
  row.className = 'leaderboard-row';

  const rankEl = document.createElement('div');
  rankEl.className = 'rank';
  rankEl.textContent = String(rank);
  row.appendChild(rankEl);

  const nameEl = document.createElement('div');
  nameEl.className = 'name';
  nameEl.textContent = entry.name;
  row.appendChild(nameEl);

  const wins = entry.realWins + entry.botWins;
  const winsEl = document.createElement('div');
  winsEl.className = 'wins';
  winsEl.textContent = `${wins} W`;
  row.appendChild(winsEl);

  const counts = document.createElement('div');
  counts.className = 'counts';
  counts.textContent =
    `Real: ${entry.realWins} wins / ${entry.realGames} games` +
    ` · Bot: ${entry.botWins} wins / ${entry.botGames} games`;
  row.appendChild(counts);
  return row;
}

function renderPad(model: GameModel): HTMLElement {
  const pad = document.createElement('div');
  pad.className = 'pad';
  pad.setAttribute('role', 'group');
  pad.setAttribute('aria-label', 'movement pad');
  const cells: Array<{ dir: number | null; label: string }> = [
    { dir: null, label: '' },
    { dir: NORTH, label: '↑' },
    { dir: null, label: '' },
    { dir: WEST, label: '←' },
    { dir: null, label: '' },
    { dir: EAST, label: '→' },
    { dir: null, label: '' },
    { dir: SOUTH, label: '↓' },
    { dir: null, label: '' },
  ];
  for (const cell of cells) {
    if (cell.dir === null) {
      const spacer = document.createElement('button');
      spacer.className = 'spacer';
      spacer.tabIndex = -1;
      pad.appendChild(spacer);
      continue;
    }
    const btn = document.createElement('button');
    btn.textContent = cell.label;
    btn.setAttribute('aria-label', `Step ${cell.label}`);
    const dir = cell.dir;
    btn.addEventListener('click', () => model.tryMove(dir));
    pad.appendChild(btn);
  }
  return pad;
}

function renderRaceControls(model: GameModel): HTMLElement {
  // `.race-controls` is what the stylesheet caps at the board's own width so
  // the Give-up button lines up with the board even when the layout stacks
  // it under a landscape row.
  const controls = document.createElement('div');
  controls.className = 'race-controls';
  controls.style.display = 'flex';
  controls.style.gap = '8px';

  const resign = document.createElement('button');
  resign.className = 'secondary';
  resign.textContent = 'Give up';
  resign.addEventListener('click', () => {
    if (model.phase !== 'playing') return;
    model.resign();
  });
  controls.appendChild(resign);
  return controls;
}

function updateHud(race: RaceView, model: GameModel): void {
  race.hud.you.textContent = `You · ${model.youMoves}`;
  race.hud.opponent.textContent = `${model.opponentName || 'Opponent'} · ${model.opponentMoves}`;
}

function updateVerdict(race: RaceView, model: GameModel): void {
  if (model.phase !== 'finished' || model.result === null) {
    race.verdict.style.display = 'none';
    race.verdict.replaceChildren();
    race.verdict.className = 'verdict';
    return;
  }

  race.verdict.className = `verdict ${model.result}`;
  race.verdict.style.display = 'flex';
  race.verdict.replaceChildren();

  const h = document.createElement('h2');
  h.textContent = model.result === 'win' ? 'You won.' : verdictHeadline(model);
  race.verdict.appendChild(h);

  const detail = document.createElement('div');
  detail.className = 'subtitle';
  detail.textContent = subheading(model);
  race.verdict.appendChild(detail);

  const playAgain = document.createElement('button');
  playAgain.textContent = 'Play again';
  playAgain.addEventListener('click', () => model.backToMenu());
  race.verdict.appendChild(playAgain);
}

function verdictHeadline(model: GameModel): string {
  switch (model.reason) {
    case 'resigned':
      return 'You gave up.';
    case 'disconnected':
      return 'They vanished.';
    default:
      return 'You lost.';
  }
}

function subheading(model: GameModel): string {
  const you = model.youMoves;
  const them = model.opponentMoves;
  const you_ = `${you} step${you === 1 ? '' : 's'}`;
  const them_ = `${them} step${them === 1 ? '' : 's'}`;
  const opponent = model.opponentName || 'Opponent';
  switch (model.reason) {
    case 'reached':
      return model.result === 'win'
        ? `${you_} · ${opponent} took ${them_}`
        : `${opponent} reached the centre in ${them_}, you were on ${you_}`;
    case 'resigned':
      return model.result === 'win'
        ? `${opponent} gave up after ${them_}. You were on ${you_}.`
        : `You gave up after ${you_}.`;
    case 'disconnected':
      return model.result === 'win'
        ? `${opponent} vanished after ${them_}.`
        : `Connection dropped.`;
    default:
      return '';
  }
}

function renderNotice(model: GameModel): HTMLElement | null {
  if (model.notice === null) return null;
  const el = document.createElement('div');
  el.className = 'notice';
  el.textContent = model.notice;
  return el;
}

/** Exported for tests that want to poke one screen. */
export const _testables = {
  renderMenu,
  renderLobby,
  renderLeaderboard,
  renderLeaderRow,
};

/** Not used at runtime; exists so `Phase` is exported for tests that import it. */
export type _Phase = Phase;
